import { ContentWrapper } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { breakpoints, Heading, Inline, Stack, vars } from '@etg/wings';
import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import type { RebookingSearchQueryVariables } from '@eti/schema-types';
import { routes } from '../../../constants/routesAndApisConstants';
import BackToPreviousPageButton from '../../../common/components/BackToPreviousPageButton';
import type { SearchTripsFormType, TripType } from '../models';
import { REBOOK_SEARCH_MORE_QUERY, REBOOK_SEARCH_QUERY } from '../containers/queries';
import getFallbackUrl from '../../../utils/getFallbackUrl';
import { quickSortType } from '../../../constants/quickSortConstants';
import { getTripRoutes } from '../utils';
import userInfo from '../../../utils/userInfo';
import OriginalTrip from './OriginalTrip';
import DatesSearchForm from './DatesSearchForm';
import RebookingResultsSection from './RebookingResultsSection';

const wrapperStyles = css`
  background-color: ${vars.colors.pageBackdrop};
  height: 100%;
  padding-top: 16px;

  @media (min-width: ${breakpoints._768}) {
    padding-top: 24px;
  }
`;

const inlineStyles = (isRebookingSearchCalled: boolean) => css`
  ${!isRebookingSearchCalled && 'margin-bottom: 32px;'};
`;

export interface ChangeTripPageProps {
  trip: TripType;
}

const ChangeTripPage = ({ trip }: ChangeTripPageProps) => {
  const { t } = useTranslation();
  const [searchVariables, setSearchVariables] = useState<RebookingSearchQueryVariables | null>(
    null,
  );
  const [rebookingSearchError, setRebookingSearchError] = useState<Error | null>(null);

  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [flightsOffset, setFlightsOffset] = useState<number | null>(null);

  const [getRebookingFlights, { data: searchData, fetchMore, loading: searchLoading, called }] =
    useLazyQuery(REBOOK_SEARCH_QUERY, {
      notifyOnNetworkStatusChange: true,
      onError: () => {
        const fallbackUrl = getFallbackUrl();
        if (fallbackUrl) {
          window.location.href = fallbackUrl;
        } else {
          setRebookingSearchError(
            new Error(
              `Invalid response while fetching rebookingSearch results: ${JSON.stringify(
                userInfo(),
              )}`,
            ),
          );
        }
      },
    });

  const onFetchMoreFlights = async () => {
    const offset = searchData?.rebookingSearch?.flights.length || 0;
    setFlightsOffset(offset);

    try {
      setIsFetchingMore(true);

      await fetchMore({
        query: REBOOK_SEARCH_MORE_QUERY,
        variables: {
          ...searchVariables,
          offset,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }

          const prevFlights = prev.rebookingSearch?.flights;
          const newFlights = fetchMoreResult.rebookingSearch?.flights;

          return {
            ...prev,
            rebookingSearch: {
              ...prev.rebookingSearch!!,
              flights: [...(prevFlights || []), ...(newFlights || [])],
            },
          };
        },
      });
      setIsFetchingMore(false);
    } catch (error) {
      setIsFetchingMore(false);
      setRebookingSearchError(error as Error);
    }
  };

  const onSearchFlights = async (formValues: SearchTripsFormType) => {
    const { departureDate, returnDate } = formValues.singleBound;
    if (!departureDate) {
      return;
    }
    const tripRoutes = getTripRoutes({ departureDate, returnDate }, trip);

    if (!tripRoutes) {
      return;
    }

    const variables = {
      filters: [],
      routes: tripRoutes,
      sortTypeCode: quickSortType.RECOMMENDED,
    };

    setSearchVariables(variables);
    await getRebookingFlights({ variables });
  };

  return (
    <div className={wrapperStyles}>
      <ContentWrapper>
        <Stack
          spacing={{
            [breakpoints._0]: 16,
            [breakpoints._360]: 24,
            [breakpoints._768]: 32,
          }}
        >
          <BackToPreviousPageButton
            dataTestId="changeTrip-back-to-order-details-button"
            linkText={t('ChangeTrip.BackToMyTrip.Text')}
            route={routes.ORDER_DETAILS_MANAGE_TRIP}
          />
          <Stack
            spacing={{
              [breakpoints._0]: 16,
              [breakpoints._768]: 24,
            }}
          >
            <Heading level={1}>{t('ChangeTrip.ReviewPrices.Title')}</Heading>
            <Inline
              className={inlineStyles(called)}
              collapseBelow={breakpoints._768}
              noWrap
              spacing={24}
            >
              <OriginalTrip originalTrip={trip} />
              <DatesSearchForm handleSearch={onSearchFlights} originalTrip={trip} />
            </Inline>
          </Stack>
          <RebookingResultsSection
            flightsOffset={flightsOffset}
            handleFetchMoreFlights={onFetchMoreFlights}
            isFetchingMore={isFetchingMore}
            isLoading={searchLoading}
            rebookingSearchError={rebookingSearchError}
            searchData={searchData?.rebookingSearch}
          />
        </Stack>
      </ContentWrapper>
    </div>
  );
};

export default ChangeTripPage;
